/**
 * Cliente do Worker para a superfície interna.
 *
 * A sessão interna chega no `?t=` posto pela função Deluge do botão do Bigin.
 * Guardamo-la em memória e limpamos o URL — um token não deve ficar no
 * histórico do browser nem viajar no referrer.
 *
 * O `?clinica=` é o atalho de desenvolvimento, e só funciona se o Worker
 * tiver PERMITIR_INTERNO_SEM_ASSINATURA=true.
 */

import {
	HEADER_SESSAO_INTERNA,
	type AssinarResposta,
	type ContextoInternoResposta,
	type ErroApi,
	type GerarLinkResposta,
	type PrepararPedido,
	type PrepararResposta
} from '@ac/shared';

const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

const parametros = new URLSearchParams(location.search);
const tokenSessao = parametros.get('t');
const clinicaDev = parametros.get('clinica');

if (tokenSessao) {
	history.replaceState(null, '', location.pathname);
}

export class ErroDoServidor extends Error {
	constructor(
		readonly codigo: ErroApi['codigo'],
		mensagem: string
	) {
		super(mensagem);
	}
}

function sufixo(): string {
	return !tokenSessao && clinicaDev ? `?clinica=${encodeURIComponent(clinicaDev)}` : '';
}

async function chamar<T>(caminho: string, init: RequestInit = {}): Promise<T> {
	const cabecalhos = new Headers(init.headers);
	cabecalhos.set('Content-Type', 'application/json');
	if (tokenSessao) cabecalhos.set(HEADER_SESSAO_INTERNA, tokenSessao);

	let resposta: Response;
	try {
		resposta = await fetch(`${BASE}${caminho}${sufixo()}`, { ...init, headers: cabecalhos });
	} catch {
		throw new ErroDoServidor('erro_interno', 'Sem ligação ao servidor');
	}

	const texto = await resposta.text();
	const corpo: unknown = texto ? JSON.parse(texto) : null;

	if (!resposta.ok) {
		const erro = corpo as ErroApi | null;
		throw new ErroDoServidor(erro?.codigo ?? 'erro_interno', erro?.erro ?? 'Erro inesperado');
	}

	return corpo as T;
}

export const api = {
	contexto: () => chamar<ContextoInternoResposta>('/api/interno/contexto'),

	preparar: (pedido: PrepararPedido) =>
		chamar<PrepararResposta>('/api/interno/preparar', {
			method: 'POST',
			body: JSON.stringify(pedido)
		}),

	assinar: (sessaoId: string, assinaturaPng: string) =>
		chamar<AssinarResposta>('/api/interno/assinar', {
			method: 'POST',
			body: JSON.stringify({ sessaoId, assinaturaPng })
		}),

	gerarLink: (sessaoId: string) =>
		chamar<GerarLinkResposta>('/api/interno/link', {
			method: 'POST',
			body: JSON.stringify({ sessaoId })
		})
};
